import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BADGE_TYPES } from '../data/badgeTypes';
import { buildCredential } from '../utils/obv3';
import { getAllBadges, createBadge, revokeBadge, deleteBadge } from '../apiBadge';
import BadgeCard from '../components/BadgeCard';

const AdminPanel = () => {
    const [recipientName, setRecipientName] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [badgeTypeId, setBadgeTypeId] = useState('');
    const [year, setYear] = useState(new Date().getFullYear().toString());
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState('');
    const [allBadges, setAllBadges] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [viewBadge, setViewBadge] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        refreshBadges();
    }, []);

    const refreshBadges = () => {
        getAllBadges().then(data => {
            if (data && !data.error) {
                setAllBadges(data);
            }
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setSuccess(null);

        if (!recipientName || !recipientEmail || !badgeTypeId || !year) {
            setError('Please fill in all required fields.');
            return;
        }

        const badgeType = BADGE_TYPES[badgeTypeId];
        if (!badgeType) {
            setError('Invalid badge type selected.');
            return;
        }

        setLoading(true);

        const credentialJson = buildCredential({
            recipientName,
            recipientEmail,
            badgeType,
            year,
            message,
        });

        createBadge({
            badgeTypeId,
            recipientName,
            recipientEmail,
            year,
            message,
            credentialJson,
        }, imageFile).then(data => {
            setLoading(false);
            if (data.error) {
                setError(data.error);
            } else {
                setSuccess(data);
                setRecipientName('');
                setRecipientEmail('');
                setMessage('');
                setImageFile(null);
                setImagePreview(null);
                refreshBadges();
            }
        });
    };

    const handleRevoke = (badgeId) => {
        revokeBadge(badgeId).then(data => {
            if (data && !data.error) {
                refreshBadges();
            }
        });
    };

    const handleDelete = (badgeId) => {
        deleteBadge(badgeId).then(data => {
            if (data && !data.error) {
                setConfirmDelete(null);
                refreshBadges();
            }
        });
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3, mb: 4, backgroundColor: 'rgba(0, 62, 126, 0.02)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <EmojiEventsIcon sx={{ fontSize: 36, color: '#FF6600' }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E' }}>
                        Issue Digital Award Badge
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Box component="form" onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Recipient Name *"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            fullWidth
                            placeholder="e.g. Jeremy Morton"
                            sx={{
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#003E7E' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#003E7E' },
                            }}
                        />
                        <TextField
                            label="Recipient Email *"
                            type="email"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            fullWidth
                            placeholder="e.g. jeremy@example.com"
                            sx={{
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#003E7E' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#003E7E' },
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControl sx={{ flex: '1 1 auto', minWidth: { xs: '100%', sm: 200 } }}>
                                <InputLabel>Award Type *</InputLabel>
                                <Select
                                    value={badgeTypeId}
                                    label="Award Type *"
                                    onChange={(e) => setBadgeTypeId(e.target.value)}
                                >
                                    {Object.values(BADGE_TYPES).map(bt => (
                                        <MenuItem key={bt.id} value={bt.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: bt.color }} />
                                                {bt.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                                <InputLabel>Year *</InputLabel>
                                <Select
                                    value={year}
                                    label="Year *"
                                    onChange={(e) => setYear(e.target.value)}
                                >
                                    {yearOptions.map(y => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <TextField
                            label="Personal Message (optional)"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="e.g. Thank you for your outstanding contributions..."
                            sx={{
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#003E7E' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#003E7E' },
                            }}
                        />
                        {/* Custom Badge Image Upload */}
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#003E7E' }}>
                                Badge Image (optional — uses default award image if not provided)
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                sx={{
                                    borderColor: '#003E7E',
                                    color: '#003E7E',
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: '#003E7E',
                                        backgroundColor: 'rgba(0, 62, 126, 0.05)',
                                    }
                                }}
                            >
                                {imageFile ? imageFile.name : 'Choose image file...'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            setImagePreview(URL.createObjectURL(file));
                                        }
                                    }}
                                />
                            </Button>
                            {imagePreview && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Box
                                        component="img"
                                        src={imagePreview}
                                        alt="Badge preview"
                                        sx={{
                                            maxWidth: 200,
                                            maxHeight: 200,
                                            borderRadius: 2,
                                            border: '2px solid #003E7E',
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={() => { setImageFile(null); setImagePreview(null); }}
                                        sx={{ display: 'block', mx: 'auto', mt: 1, color: '#d32f2f' }}
                                    >
                                        Remove image
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <EmojiEventsIcon />}
                            sx={{
                                backgroundColor: '#FF6600',
                                py: 1.5,
                                fontWeight: 700,
                                fontSize: '1rem',
                                '&:hover': { backgroundColor: '#e55a00' }
                            }}
                        >
                            {loading ? 'Issuing...' : 'Issue Badge'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Success Preview */}
            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 4 }}
                    onClose={() => setSuccess(null)}
                >
                    Badge issued successfully to {success.recipientName}!
                </Alert>
            )}
            {success && (
                <Box sx={{ maxWidth: 400, mx: 'auto', mb: 4 }}>
                    <BadgeCard badge={success} />
                </Box>
            )}

            {/* Issued Badges Table */}
            {allBadges.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                        All Issued Badges ({allBadges.length})
                    </Typography>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 550 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#003E7E' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Recipient</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Award</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Year</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allBadges.map((badge) => {
                                    const bt = BADGE_TYPES[badge.badgeTypeId];
                                    return (
                                        <TableRow key={badge._id} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {badge.recipientName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {badge.recipientEmail}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={bt?.name || badge.badgeTypeId}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: bt?.colorLight || '#eee',
                                                        color: bt?.color || '#333',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{badge.year}</TableCell>
                                            <TableCell>
                                                {badge.revoked ? (
                                                    <Chip label="Revoked" size="small" color="error" />
                                                ) : (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        icon={<CheckCircleIcon />}
                                                        sx={{
                                                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                                            color: '#388e3c',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Badge">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setViewBadge(badge)}
                                                        sx={{ color: '#003E7E' }}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {!badge.revoked && (
                                                    <Tooltip title="Revoke">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRevoke(badge._id)}
                                                            sx={{ color: '#f57c00' }}
                                                        >
                                                            <BlockIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setConfirmDelete(badge._id)}
                                                        sx={{ color: '#d32f2f' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete Badge?</DialogTitle>
                <DialogContent>
                    <Typography>This will permanently remove this badge. This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button onClick={() => handleDelete(confirmDelete)} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Badge Dialog */}
            <Dialog
                open={!!viewBadge}
                onClose={() => setViewBadge(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiEventsIcon sx={{ color: '#FF6600' }} />
                    Award Badge Preview
                </DialogTitle>
                <DialogContent>
                    {viewBadge && <BadgeCard badge={viewBadge} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewBadge(null)} variant="outlined">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminPanel;
